import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CustomerImportResult,
  DataApiResponseLegacy,
  EmailTag,
  NewsletterAiGenerateRequest,
  NewsletterAiGenerateResponse,
  NewsletterAiSettings,
  NewsletterCampaign,
  NewsletterCampaignAnalytics,
  NewsletterCampaignKind,
  NewsletterCampaignStatus,
  NewsletterComponentPreview,
  NewsletterComponentStills,
  NewsletterCopyFromRequest,
  NewsletterCustomerAdd,
  NewsletterCustomerNewsletter,
  NewsletterDefaultSectionOrderItem,
  NewsletterDiscountOption,
  NewsletterEmailConfig,
  NewsletterFooter,
  NewsletterGenerateRequest,
  NewsletterGenerationRecipient,
  NewsletterGenerationSummary,
  NewsletterLinkSettings,
  NewsletterPreview,
  NewsletterRecipientOrigin,
  NewsletterScheduleRequest,
  NewsletterSuppression,
  NewsletterSuppressionReason,
  NewsletterTestSendRequest,
  PostNewsletterCampaign,
  PostNewsletterComponent,
  PutNewsletterCampaign,
  PutNewsletterComponent,
  PutNewsletterComponentFilm,
  PutNewsletterComponentSettings,
  PutNewsletterEmailConfig,
} from '@digilize/shared/definitions/src';

@Injectable({ providedIn: 'root' })
export class NewsletterApiService {
  private readonly baseUrl = '/api/v1.0/newsletter';

  constructor(private http: HttpClient) {}

  getCampaigns(params?: GetNewsletterCampaignsQuery) {
    return this.http.get<DataApiResponseLegacy<NewsletterCampaign[]>>(`${this.baseUrl}/campaigns`, {
      params: { ...params },
    });
  }

  getCampaign(id: string) {
    return this.http.get<NewsletterCampaign>(`${this.baseUrl}/campaigns/${id}`);
  }

  addCampaign(data: PostNewsletterCampaign) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns`, data);
  }

  editCampaign(id: string, data: PutNewsletterCampaign) {
    return this.http.put<NewsletterCampaign>(`${this.baseUrl}/campaigns/${id}`, data);
  }

  deleteCampaign(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/campaigns/${id}`);
  }

  getCampaignAnalytics(id: string) {
    return this.http.get<NewsletterCampaignAnalytics>(`${this.baseUrl}/campaigns/${id}/analytics`);
  }

  /** The tag catalogue behind the insert-field menus (subject, preheader, section text). */
  getTags() {
    return this.http.get<EmailTag[]>(`${this.baseUrl}/tags`);
  }

  /** Discounts a newsletter section of the company may link: active, of type unique code (PAUL-3690). */
  getPickableDiscounts(companyId: string) {
    return this.http.get<NewsletterDiscountOption[]>(`${this.baseUrl}/discounts`, {
      params: { company_id: companyId },
    });
  }

  addComponent(campaignId: string, data: PostNewsletterComponent) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/components`, data);
  }

  editComponent(campaignId: string, componentId: string, data: PutNewsletterComponent) {
    return this.http.put<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/components/${componentId}`, data);
  }

  /**
   * The section settings dialog's save (PAUL-3690) - the same components PUT, with the stored content
   * echoed back, the section's discount and a targeting signal. The PUT assigns every content field
   * from the body, so a targeting-only body would wipe the section's text.
   */
  editComponentSettings(campaignId: string, componentId: string, data: PutNewsletterComponentSettings) {
    return this.http.put<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/components/${componentId}`, data);
  }

  deleteComponent(campaignId: string, componentId: string) {
    return this.http.delete<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/components/${componentId}`);
  }

  reorderComponents(campaignId: string, componentIds: string[]) {
    return this.http.put<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/components/order`, componentIds);
  }

  copyFromComponent(campaignId: string, data: NewsletterCopyFromRequest) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/components/copy-from`, data);
  }

  editComponentFilm(campaignId: string, componentId: string, data: PutNewsletterComponentFilm) {
    return this.http.put<NewsletterCampaign>(
      `${this.baseUrl}/campaigns/${campaignId}/components/${componentId}/film`,
      data
    );
  }

  getComponentPreview(campaignId: string, componentId: string) {
    return this.http.get<NewsletterComponentPreview>(
      `${this.baseUrl}/campaigns/${campaignId}/components/${componentId}/preview`
    );
  }

  getComponentStills(campaignId: string, componentId: string) {
    return this.http.get<NewsletterComponentStills>(
      `${this.baseUrl}/campaigns/${campaignId}/components/${componentId}/stills`
    );
  }

  getCampaignPreview(campaignId: string) {
    return this.http.get<NewsletterPreview>(`${this.baseUrl}/campaigns/${campaignId}/preview`);
  }

  getGenerationPreview(campaignId: string) {
    return this.http.get<NewsletterPreview>(`${this.baseUrl}/campaigns/${campaignId}/generation/preview`);
  }

  getGeneration(campaignId: string) {
    return this.http.get<NewsletterGenerationSummary>(`${this.baseUrl}/campaigns/${campaignId}/generation`);
  }

  getGenerationRecipients(campaignId: string, params?: GetNewsletterRecipientsQuery) {
    return this.http.get<DataApiResponseLegacy<NewsletterGenerationRecipient[]>>(
      `${this.baseUrl}/campaigns/${campaignId}/generation/recipients`,
      { params: { ...params } }
    );
  }

  prepareCampaign(campaignId: string) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/prepare`, {});
  }

  backToDraftCampaign(campaignId: string) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/back-to-draft`, {});
  }

  /**
   * Bodiless POST reuses the campaign's stored selection; passing `selection` (even `{}`)
   * makes that body the selection, so a null `audience_list_id` in it clears a stored one
   * (handover §11).
   */
  generateCampaign(campaignId: string, selection?: NewsletterGenerateRequest) {
    return this.http.post<NewsletterGenerationSummary>(
      `${this.baseUrl}/campaigns/${campaignId}/generate`,
      selection ?? null
    );
  }

  rollbackCampaign(campaignId: string) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/rollback`, {});
  }

  sendCampaign(campaignId: string) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/send`, {});
  }

  /** Re-dispatches the recipients a held run has not sent yet. */
  resumeCampaign(campaignId: string) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/resume`, {});
  }

  /** Drops the unsent tail so the run can complete; what the sender already has still delivers. */
  stopCampaign(campaignId: string) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/stop`, {});
  }

  scheduleCampaign(campaignId: string, data: NewsletterScheduleRequest) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/schedule`, data);
  }

  cancelScheduleCampaign(campaignId: string) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/cancel-schedule`, {});
  }

  declineCampaign(campaignId: string) {
    return this.http.post<NewsletterCampaign>(`${this.baseUrl}/campaigns/${campaignId}/decline`, {});
  }

  testSendCampaign(campaignId: string, data: NewsletterTestSendRequest) {
    return this.http.post<void>(`${this.baseUrl}/campaigns/${campaignId}/test-send`, data);
  }

  aiGenerateComponent(campaignId: string, componentId: string, data: NewsletterAiGenerateRequest) {
    return this.http.post<NewsletterAiGenerateResponse>(
      `${this.baseUrl}/campaigns/${campaignId}/components/${componentId}/ai-generate`,
      data
    );
  }

  aiGenerateCampaign(campaignId: string, data: NewsletterAiGenerateRequest) {
    return this.http.post<NewsletterAiGenerateResponse>(`${this.baseUrl}/campaigns/${campaignId}/ai-generate`, data);
  }

  getDefaultSectionOrder(companyId: string) {
    return this.http.get<NewsletterDefaultSectionOrderItem[]>(`${this.baseUrl}/default-section-order`, {
      params: { companyId },
    });
  }

  editDefaultSectionOrder(companyId: string, data: NewsletterDefaultSectionOrderItem[]) {
    return this.http.put<NewsletterDefaultSectionOrderItem[]>(`${this.baseUrl}/default-section-order`, data, {
      params: { companyId },
    });
  }

  getFooter(companyId: string) {
    return this.http.get<NewsletterFooter>(`${this.baseUrl}/footer/${companyId}`);
  }

  editFooter(companyId: string, data: NewsletterFooter) {
    return this.http.put<NewsletterFooter>(`${this.baseUrl}/footer/${companyId}`, data);
  }

  getAiSettings(companyId: string) {
    return this.http.get<NewsletterAiSettings>(`${this.baseUrl}/ai-settings/${companyId}`);
  }

  editAiSettings(companyId: string, data: NewsletterAiSettings) {
    return this.http.put<NewsletterAiSettings>(`${this.baseUrl}/ai-settings/${companyId}`, data);
  }

  getEmailConfig(companyId: string) {
    return this.http.get<NewsletterEmailConfig>(`${this.baseUrl}/email-config/${companyId}`);
  }

  editEmailConfig(companyId: string, data: PutNewsletterEmailConfig) {
    return this.http.put<NewsletterEmailConfig>(`${this.baseUrl}/email-config/${companyId}`, data);
  }

  getLinkSettings(shopId: string) {
    return this.http.get<NewsletterLinkSettings>(`${this.baseUrl}/link-settings/${shopId}`);
  }

  editLinkSettings(shopId: string, data: NewsletterLinkSettings) {
    return this.http.put<NewsletterLinkSettings>(`${this.baseUrl}/link-settings/${shopId}`, data);
  }

  getSuppressions(params?: GetNewsletterSuppressionsQuery) {
    return this.http.get<DataApiResponseLegacy<NewsletterSuppression[]>>(`${this.baseUrl}/suppressions`, {
      params: { ...params },
    });
  }

  deleteSuppression(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/suppressions/${id}`);
  }

  importCustomers(shopId: string, file: File) {
    const formData = new FormData();
    formData.append('ShopId', shopId);
    formData.append('File', file);
    return this.http.post<CustomerImportResult>(`${this.baseUrl}/customers/import`, formData);
  }

  addSubscriber(data: NewsletterCustomerAdd) {
    return this.http.post<void>(`${this.baseUrl}/customers/subscribers`, data);
  }

  setCustomerNewsletter(data: NewsletterCustomerNewsletter) {
    return this.http.put<void>(`${this.baseUrl}/customers/newsletter`, data);
  }
}

export interface GetNewsletterCampaignsQuery {
  company?: string;
  status?: NewsletterCampaignStatus;
  /** PAUL-3689: 0 newsletter, 1 service mail; omitted for both. */
  kind?: NewsletterCampaignKind;
  'search.value'?: string;
  order_column_name?: string;
  order_column_descending?: boolean;
  draw?: number;
  start?: number;
  length?: number;
}

export interface GetNewsletterRecipientsQuery {
  origin?: NewsletterRecipientOrigin;
  excluded?: boolean;
  'search.value'?: string;
  order_column_name?: string;
  order_column_descending?: boolean;
  draw?: number;
  start?: number;
  length?: number;
}

export interface GetNewsletterSuppressionsQuery {
  reason?: NewsletterSuppressionReason;
  companyId?: string;
  'search.value'?: string;
  order_column_name?: string;
  order_column_descending?: boolean;
  draw?: number;
  start?: number;
  length?: number;
}
